import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Trash2, Plus, RotateCcw, Package, AlertCircle, ShoppingBag, Edit3, Check, Truck, FileText, Image as ImageIcon } from 'lucide-react';
import { useRMACart, RMA_NOTE_MAX } from '../../context/RMACartContext';
import { useAuth } from '../../context/AuthContext';
import { AddToRMAModal } from '../../components/rma/AddToRMAModal';
import { RMAMotivo } from '../../types';
import { createRMA, fetchRegolaSpedizioneReso } from '../../services/rmaService';
import type { RegolaSpedizioneReso } from '../../services/rmaService';
import type { SpedizioneReso } from '../../types';
import { toast } from 'sonner';
import { numeroDocumento } from '../../utils/numeroDocumento';

const MOTIVI_LABEL: Record<string, string> = {
  difetto_stampa: 'Difetto di stampa',
  prodotto_danneggiato: 'Prodotto danneggiato',
  qualita_non_conforme: 'Qualità non conforme',
  articolo_errato_ricevuto: 'Articolo errato ricevuto',
  articolo_ordinato_erroneamente: 'Articolo ordinato erroneamente',
  quantita_errata: 'Quantità errata',
  altro: 'Altro',
};

const MOTIVI_REQUIRING_PHOTO: RMAMotivo[] = ['difetto_stampa', 'prodotto_danneggiato', 'qualita_non_conforme', 'articolo_errato_ricevuto'];

export default function RMACartPage() {
  const navigate = useNavigate();
  const { items, removeItem, updateItem, clearCart, totalUnits, totalLines, totalValue } = useRMACart();
  const { canOpenRma } = useAuth();
  const [editProduct, setEditProduct] = useState<any>(null);
  const [accepted, setAccepted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState<string | null>(null);
  // Chi spedisce la merce (INT-129): per la pratica appena inviata lo dice il
  // server, sui prezzi dei documenti (clearCart() azzera intanto il
  // totalValue); per il carrello, la regola chiesta al server.
  const [submittedShipping, setSubmittedShipping] = useState<SpedizioneReso | null>(null);
  const [regolaSpedizione, setRegolaSpedizione] = useState<RegolaSpedizioneReso | null>(null);
  useEffect(() => {
    fetchRegolaSpedizioneReso().then(setRegolaSpedizione).catch(() => setRegolaSpedizione(null));
  }, []);

  // Raggruppa per ordine
  const groupedByOrder = useMemo(() => {
    const groups: Record<string, { orderDate: string; orderStatus: string; items: typeof items }> = {};
    for (const it of items) {
      if (!groups[it.orderId]) {
        groups[it.orderId] = { orderDate: it.orderDate, orderStatus: it.orderStatus, items: [] };
      }
      groups[it.orderId].items.push(it);
    }
    return groups;
  }, [items]);

  // Validazione: ogni unit deve avere motivo + foto se richiesta
  const isLineValid = (line: typeof items[0]): boolean => {
    return line.units.every(u => {
      if (!u.motivo) return false;
      if (MOTIVI_REQUIRING_PHOTO.includes(u.motivo as RMAMotivo) && !u.photoUrl) return false;
      return true;
    });
  };

  const allValid = items.length > 0 && items.every(isLineValid);

  const removeUnit = (orderId: string, sku: string, unitId: string) => {
    const line = items.find(i => i.orderId === orderId && i.sku === sku);
    if (!line) return;
    const newUnits = line.units.filter(u => u.id !== unitId);
    if (newUnits.length === 0) {
      removeItem(orderId, sku);
      toast.info('Articolo rimosso dal carrello resi');
    } else {
      updateItem(orderId, sku, { units: newUnits });
    }
  };

  const updateUnitNote = (orderId: string, sku: string, unitId: string, descrizione: string) => {
    const line = items.find(i => i.orderId === orderId && i.sku === sku);
    if (!line) return;
    updateItem(orderId, sku, { units: line.units.map(u => (u.id === unitId ? { ...u, descrizione } : u)) });
  };

  const handleClearAll = () => {
    if (confirm('Sicuro di voler svuotare il carrello resi? Tutti gli articoli e i dettagli inseriti andranno persi.')) {
      clearCart();
      toast.info('Carrello resi svuotato');
    }
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const created = await createRMA(
        items.map(line => ({
          sku: line.sku,
          title: line.title,
          brand: line.brand,
          category: line.category,
          orderId: line.orderId,
          orderDate: line.orderDate,
          orderStatus: line.orderStatus,
          docTipork: line.docTipork,
          docAnno: line.docAnno,
          docSerie: line.docSerie,
          docNumdoc: line.docNumdoc,
          unitPrice: line.unitPrice,
          units: line.units.map(u => ({
            id: u.id,
            motivo: u.motivo as string,
            descrizione: u.descrizione,
            photoPath: u.photoPath || null,
            // Un carrello salvato prima dell'Object Storage ha solo l'URL vecchio:
            // il backend ne ricava la chiave.
            photoUrl: u.photoPath ? null : u.photoUrl || null,
            photoName: u.photoName || null,
            photoMime: u.photoMime || null,
          })),
        }))
      );
      setSubmittedShipping(created.spedizioneReso ?? null);
      clearCart();
      setDone(created.numero || created.id);
      toast.success(`Pratica ${created.numero || created.id} inviata con successo!`);
    } catch (err: any) {
      toast.error('Errore durante l\'invio della pratica RMA', {
        description: err?.response?.data?.detail || 'Riprova più tardi.',
      });
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-16 text-center" data-testid="rma-confirm-success">
        <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-5">
          <Check className="w-8 h-8 text-emerald-600" />
        </div>
        <h1 className="text-2xl font-semibold mb-2">Pratica RMA inviata!</h1>
        <p className="text-lg font-mono font-bold text-[hsl(214,58%,41%)] mb-3">
          <Link to={`/area-clienti/rma/${done}`} className="hover:underline" data-testid="rma-confirm-link">{done}</Link>
        </p>
        <p className="text-sm text-muted-foreground mb-8 max-w-md mx-auto">
          La tua richiesta è stata inviata ed è in attesa di valutazione. Riceverai un'email di conferma e potrai monitorare lo stato dalla sezione RMA.
        </p>
        <div className="rounded-xl border bg-white p-5 max-w-md mx-auto mb-6 text-left">
          <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">Prossimi passi</div>
          <div className="space-y-2 text-sm">
            <div className="flex items-start gap-2"><FileText className="w-4 h-4 text-[hsl(214,58%,41%)] flex-shrink-0 mt-0.5" /><span>Riceverai autorizzazione dopo verifica entro 5 giorni lavorativi</span></div>
            {submittedShipping && (
              <div className="flex items-start gap-2" data-testid="rma-confirm-spedizione"><Truck className="w-4 h-4 text-[hsl(214,58%,41%)] flex-shrink-0 mt-0.5" /><span>{submittedShipping.testo}</span></div>
            )}
            <div className="flex items-start gap-2"><RotateCcw className="w-4 h-4 text-[hsl(214,58%,41%)] flex-shrink-0 mt-0.5" /><span>L'accredito o sostituzione avverrà dopo il controllo qualità</span></div>
          </div>
        </div>
        <div className="flex items-center justify-center gap-3">
          <Link to="/area-clienti/rma" className="h-9 px-4 rounded-lg bg-[hsl(214,58%,41%)] text-white text-sm font-medium inline-flex items-center hover:bg-[hsl(214,58%,46%)] transition-colors">Vai alle pratiche RMA</Link>
          <Link to="/" className="h-9 px-4 rounded-lg border text-sm font-medium inline-flex items-center hover:bg-secondary/60 transition-colors">Torna alla home</Link>
        </div>
      </div>
    );
  }


  if (items.length === 0) {
    return (
      <div data-testid="rma-cart-empty">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-20 text-center">
          <ShoppingBag className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
          <h1 className="text-2xl font-semibold mb-2">Carrello resi vuoto</h1>
          <p className="text-sm text-muted-foreground mb-6">Aggiungi prodotti da una o più fatture per iniziare la pratica di reso. Puoi continuare ad aggiungere articoli finché non chiudi l'RMA.</p>
          <div className="flex items-center justify-center gap-3">
            <Link to="/area-clienti/rma/da-documento" className="h-9 px-4 rounded-lg bg-[hsl(214,58%,41%)] text-white text-sm font-medium inline-flex items-center gap-1.5 hover:bg-[hsl(214,58%,46%)] transition-colors">
              <FileText className="w-3.5 h-3.5" /> Aggiungi da documento
            </Link>
            <Link to="/area-clienti/rma/cerca" className="h-9 px-4 rounded-lg border text-sm font-medium inline-flex items-center hover:bg-secondary/60 transition-colors">
              Cerca prodotto
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="rma-cart-page">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8">
        <Link to="/area-clienti/rma" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-4">
          <ArrowLeft className="w-3.5 h-3.5" /> Torna a RMA / Resi
        </Link>

        <div className="flex items-start justify-between gap-4 mb-6 flex-wrap">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-xl bg-emerald-100 flex items-center justify-center flex-shrink-0">
              <RotateCcw className="w-5 h-5 text-emerald-700" />
            </div>
            <div>
              <h1 className="text-2xl font-semibold tracking-tight">Carrello resi</h1>
              <p className="text-sm text-muted-foreground">Pratica RMA in preparazione · {totalUnits} pz · {totalLines} {totalLines === 1 ? 'articolo' : 'articoli'} da {Object.keys(groupedByOrder).length} {Object.keys(groupedByOrder).length === 1 ? 'documento' : 'documenti'}</p>
            </div>
          </div>
          <button
            onClick={handleClearAll}
            className="h-9 px-3 rounded-lg border text-xs font-medium flex items-center gap-1.5 hover:bg-red-50 hover:border-red-200 hover:text-red-700 transition-colors"
            data-testid="rma-cart-clear"
          >
            <Trash2 className="w-3.5 h-3.5" /> Svuota
          </button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Lista raggruppata per ordine */}
          <div className="lg:col-span-2 space-y-5">
            {Object.entries(groupedByOrder).map(([orderId, group]) => {
              const orderDateFmt = new Date(group.orderDate).toLocaleDateString('it-IT', { day: '2-digit', month: 'short', year: 'numeric' });
              return (
                <div key={orderId} className="rounded-xl border bg-white overflow-hidden" data-testid={`rma-cart-order-${orderId}`}>
                  {/* Order header */}
                  <div className="bg-[hsl(var(--dtk-canvas))] border-b px-4 py-2.5 flex items-center justify-between">
                    <div>
                      <span className="text-xs font-mono font-bold">{numeroDocumento(orderId)}</span>
                      <span className="text-xs text-muted-foreground ml-2">{orderDateFmt}</span>
                    </div>
                    {group.items[0]?.docTipork ? (
                      <Link
                        to={`/area-clienti/rma/da-documento/${group.items[0].docTipork}/${group.items[0].docAnno}/${encodeURIComponent(group.items[0].docSerie || ' ')}/${group.items[0].docNumdoc}`}
                        className="text-[11px] text-[hsl(214,58%,46%)] hover:underline font-medium flex items-center gap-1">
                        <Plus className="w-3 h-3" /> Aggiungi altri da questo documento
                      </Link>
                    ) : null}
                  </div>

                  {/* Lines */}
                  {group.items.map(line => {
                    const valid = isLineValid(line);
                    return (
                      <div key={`${line.orderId}-${line.sku}`} className="border-b last:border-b-0 p-4">
                        <div className="flex items-start gap-3 mb-3">
                          <div className="w-9 h-9 rounded-lg bg-secondary/60 flex items-center justify-center flex-shrink-0">
                            <Package className="w-4 h-4 text-muted-foreground" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap mb-0.5">
                              {line.dtkCodice && <span className="font-mono text-[10px] font-bold text-[hsl(214,58%,41%)]">DTK: {line.dtkCodice}</span>}
                              <span className="font-mono text-[10px] text-muted-foreground">Cod. {line.sku}</span>
                              <span className="text-[10px] font-bold border px-1.5 py-0.5 rounded bg-secondary/60">{line.brand}</span>
                            </div>
                            <div className="text-sm font-medium">{line.title}</div>
                            <div className="text-xs text-muted-foreground mt-0.5">
                              {line.units.length} pz da rendere{' '}
                              {line.units.length > 1 ? (
                                <>· Totale: <span className="font-semibold text-foreground">€ {(line.unitPrice * line.units.length).toFixed(2).replace('.', ',')}</span></>
                              ) : (
                                <>· € {(line.unitPrice * line.units.length).toFixed(2).replace('.', ',')}</>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-1 flex-shrink-0">
                            <button
                              onClick={() => setEditProduct({
                                sku: line.sku, title: line.title, brand: line.brand, category: line.category,
                                dtkCodice: line.dtkCodice, codiceOem: line.codiceOem,
                                orderId: line.orderId, orderDate: line.orderDate, orderStatus: line.orderStatus,
                                docTipork: line.docTipork, docAnno: line.docAnno, docSerie: line.docSerie, docNumdoc: line.docNumdoc,
                                maxQty: line.maxQty, qtyPurchased: line.qtyPurchased, unitPrice: line.unitPrice,
                              })}
                              className="w-8 h-8 rounded-lg hover:bg-secondary flex items-center justify-center text-muted-foreground hover:text-foreground"
                              title="Aggiungi altri pezzi di questo prodotto"
                              data-testid={`rma-cart-edit-${line.sku}`}
                            >
                              <Plus className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => { removeItem(line.orderId, line.sku); toast.info('Articolo rimosso'); }}
                              className="w-8 h-8 rounded-lg hover:bg-red-50 hover:text-red-600 flex items-center justify-center text-muted-foreground"
                              data-testid={`rma-cart-remove-${line.sku}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </div>

                        {/* Per-unit chips */}
                        <div className="space-y-1.5 pl-12">
                          {line.units.map((u, idx) => {
                            const motivoLabel = u.motivo ? MOTIVI_LABEL[u.motivo] : null;
                            const photoNeeded = MOTIVI_REQUIRING_PHOTO.includes(u.motivo as RMAMotivo) && !u.photoUrl;
                            const unitValid = u.motivo && !photoNeeded;
                            return (
                              <div key={u.id} className={`rounded-lg border px-3 py-2 space-y-1.5 text-xs ${unitValid ? 'bg-white' : 'bg-amber-50 border-amber-200'}`}>
                                <div className="flex items-center gap-2">
                                  <span className="w-5 h-5 rounded-full bg-[hsl(214,58%,41%)] text-white text-[10px] font-bold flex items-center justify-center flex-shrink-0">
                                    {idx + 1}
                                  </span>
                                  {motivoLabel ? (
                                    <span className="font-medium">{motivoLabel}</span>
                                  ) : (
                                    <span className="text-amber-700 font-medium flex items-center gap-1">
                                      <AlertCircle className="w-3 h-3" /> Motivo mancante
                                    </span>
                                  )}
                                  {u.photoUrl && (
                                    <span className="ml-auto inline-flex items-center gap-1 text-emerald-600 text-xs" data-testid={`rma-cart-unit-attachment-ok-${line.sku}-${idx}`}>
                                      {u.photoMime === 'application/pdf' ? (
                                        <><FileText className="w-3.5 h-3.5" strokeWidth={2} /> Allegato OK</>
                                      ) : (
                                        <><ImageIcon className="w-3.5 h-3.5" strokeWidth={2} /> Foto OK</>
                                      )}
                                    </span>
                                  )}
                                  {photoNeeded && (
                                    <span className="ml-auto inline-flex items-center gap-1 text-red-600 text-xs font-medium">
                                      <FileText className="w-3.5 h-3.5" strokeWidth={2} /> Foto/allegato richiesto
                                    </span>
                                  )}
                                  <button
                                    onClick={() => removeUnit(line.orderId, line.sku, u.id)}
                                    className="ml-2 text-muted-foreground hover:text-red-600 flex-shrink-0"
                                    title="Rimuovi pezzo"
                                    data-testid={`rma-cart-unit-remove-${line.sku}-${idx}`}
                                  >
                                    <Trash2 className="w-3 h-3" />
                                  </button>
                                </div>
                                {/* Note del pezzo (INT-128): modificabili fino alla conferma. */}
                                <textarea
                                  value={u.descrizione}
                                  onChange={e => updateUnitNote(line.orderId, line.sku, u.id, e.target.value)}
                                  maxLength={RMA_NOTE_MAX}
                                  rows={1}
                                  placeholder="Note (facoltative)"
                                  aria-label={`Note del pezzo ${idx + 1}`}
                                  className="w-full px-2 py-1.5 rounded-md border text-xs bg-white resize-y focus:outline-none focus:ring-2 focus:ring-[hsl(var(--dtk-focus))]"
                                  data-testid={`rma-cart-unit-note-${line.sku}-${idx}`}
                                />
                              </div>
                            );
                          })}
                        </div>

                        {!valid && (
                          <div className="ml-12 mt-2 text-[11px] text-amber-700 flex items-center gap-1">
                            <AlertCircle className="w-3 h-3" /> Completa motivi e foto richieste prima di confermare
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              );
            })}

            {/* CTA aggiungi altri */}
            <div className="rounded-xl border-2 border-dashed bg-white p-5 text-center">
              <p className="text-xs text-muted-foreground mb-3">Vuoi aggiungere altri prodotti al reso?</p>
              <div className="flex items-center justify-center gap-2">
                <Link to="/area-clienti/rma/da-documento" className="h-9 px-4 rounded-lg border text-xs font-medium inline-flex items-center gap-1.5 hover:bg-secondary/60 transition-colors" data-testid="rma-cart-add-from-order">
                  <Plus className="w-3.5 h-3.5" /> Aggiungi da un altro documento
                </Link>
                <Link to="/area-clienti/rma/cerca" className="h-9 px-4 rounded-lg border text-xs font-medium inline-flex items-center gap-1.5 hover:bg-secondary/60 transition-colors" data-testid="rma-cart-add-from-search">
                  Cerca prodotto
                </Link>
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <aside className="lg:col-span-1">
            <div className="rounded-xl border bg-white p-5 sticky top-4">
              <h2 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground mb-3">Riepilogo</h2>
              <div className="space-y-2 text-sm border-b pb-4 mb-4">
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">Documenti coinvolti</span>
                  <span className="font-medium">{Object.keys(groupedByOrder).length}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">Articoli</span>
                  <span className="font-medium">{totalLines}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">Pezzi totali</span>
                  <span className="font-medium">{totalUnits}</span>
                </div>
                <div className="flex items-center justify-between pt-2 text-base">
                  <span className="font-semibold">Valore stimato <span className="text-[10px] font-normal text-muted-foreground">(IVA esclusa)</span></span>
                  <span className="font-bold text-[hsl(214,58%,41%)]">€ {totalValue.toFixed(2).replace('.', ',')}</span>
                </div>
              </div>

              {!allValid && (
                <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 mb-3 flex items-start gap-2">
                  <AlertCircle className="w-3.5 h-3.5 text-amber-600 flex-shrink-0 mt-0.5" />
                  <div className="text-[11px] text-amber-900">
                    Completa motivo e foto su tutti i pezzi prima di procedere alla conferma.
                  </div>
                </div>
              )}

              {allValid && regolaSpedizione && (
                <div className="rounded-lg bg-[hsl(var(--dtk-canvas))] p-3 mb-4 text-xs space-y-1.5" data-testid="rma-cart-spedizione">
                  <div className="flex items-start gap-2">
                    <Truck className="w-3.5 h-3.5 text-[hsl(214,58%,41%)] flex-shrink-0 mt-0.5" />
                    <span>
                      <span className="font-semibold">Spedizione di reso:</span>{' '}
                      {totalValue >= regolaSpedizione.soglia ? regolaSpedizione.testoSopra : regolaSpedizione.testoSotto}
                    </span>
                  </div>
                </div>
              )}

              {allValid && (
                <label className="flex items-start gap-2 cursor-pointer mb-4">
                  <input
                    type="checkbox"
                    checked={accepted}
                    onChange={e => setAccepted(e.target.checked)}
                    className="mt-0.5"
                    data-testid="rma-cart-accept"
                  />
                  <span className="text-[11px] text-muted-foreground leading-snug">
                    Confermo che le informazioni inserite sono corrette e accetto le{' '}
                    <Link to="/legale/condizioni" className="text-[hsl(214,58%,46%)] hover:underline">condizioni di reso</Link>.
                  </span>
                </label>
              )}

              <button
                onClick={handleSubmit}
                disabled={!canOpenRma || !allValid || !accepted || submitting}
                className="w-full h-11 rounded-lg bg-[hsl(214,58%,41%)] text-white text-sm font-semibold flex items-center justify-center gap-2 hover:bg-[hsl(214,58%,46%)] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                data-testid="rma-cart-proceed-confirm"
              >
                {submitting
                  ? <><div className="w-4 h-4 rounded-full border-2 border-white border-t-transparent animate-spin" /> Invio in corso...</>
                  : <><Check className="w-4 h-4" /> Conferma e invia RMA</>}
              </button>

              {!canOpenRma && (
                <p className="text-[11px] text-amber-700 mt-2 text-center" data-testid="rma-cart-not-allowed">
                  Il tuo profilo non è abilitato ad aprire resi.
                </p>
              )}
              <p className="text-[10px] text-muted-foreground mt-3 text-center leading-relaxed">
                Premendo "Conferma", la pratica viene inviata e il carrello resi sarà svuotato.
              </p>
            </div>
          </aside>
        </div>
      </div>

      <AddToRMAModal open={!!editProduct} onClose={() => setEditProduct(null)} product={editProduct} />
    </div>
  );
}
